import { Row } from '@tanstack/react-table';
import moment from 'moment';
import {
  DEFAULT_TIME_PERIOD,
  GRID_TIME_SLOT_DEFAULT_VALUE,
  GRID_VIEW_TIME_SLOTS,
} from '../constants/settings';
import { AppointmentSlot, TimePeriod } from '../types/appointments';
import { TimerData } from '../types/common';

// function to convert time into 12 hours format
export const convertTimeTo12Hrs = (date: Date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  let meridian: 'AM' | 'PM' = 'AM';

  if (hours === 12) {
    meridian = 'PM';
  } else if (hours > 12) {
    hours -= 12;
    meridian = 'PM';
  }

  return { hours, minutes, meridian };
};

// function to convert time into 24 hours format
export const convertTimeTo24Hrs = (timeStr: string) => {
  const [time, meridian] = timeStr.split(' ');
  const [hours, minutes] = time.split(':');

  let convertibleHrs = +hours;

  if (meridian === 'PM' && convertibleHrs < 12) {
    convertibleHrs += 12;
  }

  return `${convertibleHrs}:${minutes}`;
};

// function to display time in 12 hrs format declared here
export const showTimeIn12HrsFormat = (time: string): string => {
  const [hours, minutes] = time.split(':');

  if (+hours > 12) {
    return `${+hours - 12}:${minutes} PM`;
  } else if (+hours === 12) {
    return `${+hours}:${minutes} PM`;
  }

  return `${+hours}:${minutes} AM`;
};

// function to sort table data by date declared here
export const sortByDate = (
  rowA: Row<any>,
  rowB: Row<any>,
  columnId: string
) => {
  return (
    +new Date(
      moment(rowA.original[columnId], 'DD-MM-YYYY').format('YYYY-MM-DD')
    ) -
    +new Date(
      moment(rowB.original[columnId], 'DD-MM-YYYY').format('YYYY-MM-DD')
    )
  );
};

// function to sort table data by time declared here
export const sortByTime = (
  rowA: Row<any>,
  rowB: Row<any>,
  columnId: string
) => {
  const timeA = new Date(`1970/01/01 ${rowA.original[columnId]}`);
  const timeB = new Date(`1970/01/01 ${rowB.original[columnId]}`);

  return +timeA - +timeB;
};

// function to compare two date strings declared here
export const compareTwoDates = (dateA: string, dateB: string): number => {
  const dateAObj = moment(dateA, 'DD-MM-YYYY');
  const dateBObj = moment(dateB, 'DD-MM-YYYY');

  return dateAObj.diff(dateBObj, 'days');
};

// function to extract time range from time period string (Morning | Noon | Evening) declared here
export const extractTimeFromTimePeriod = (
  timePeriod: 'Morning' | 'Noon' | 'Evening'
) => {
  let timeFilters: TimePeriod = DEFAULT_TIME_PERIOD;

  switch (timePeriod) {
    case 'Morning':
      timeFilters = {
        slot_start_time: '05:00',
        slot_end_time: '11:59',
      };
      break;

    case 'Noon':
      timeFilters = {
        slot_start_time: '12:00',
        slot_end_time: '17:59',
      };
      break;

    case 'Evening':
      timeFilters = {
        slot_start_time: '18:00',
        slot_end_time: '22:00',
      };
      break;

    default:
      break;
  }

  return timeFilters;
};

// function to convert time slots into 24 hrs time format declared here
export const convertTimeSlotInto24HrsTime = (
  timeSlot: string
): { start_time: string; end_time: string } => {
  const [startingTime, endingTime] = timeSlot.split(' - ');

  return {
    start_time: convertTimeTo24Hrs(startingTime),
    end_time: convertTimeTo24Hrs(endingTime),
  };
};

// function to check if associate have available time slots in given time duration declared here
export const hasAvailableSlotsInTimeGrid = (
  slotStartingTimes?: Array<number>
): Array<{ start_time: number; end_time: number; has_slot: boolean }> => {
  let slotAvailability: Array<boolean> = [...GRID_TIME_SLOT_DEFAULT_VALUE];

  if (slotStartingTimes?.length) {
    GRID_VIEW_TIME_SLOTS.forEach((gridSlot, index) => {
      slotStartingTimes.forEach((sStartTime) => {
        if (
          sStartTime >= gridSlot.start_time &&
          sStartTime <= gridSlot.end_time
        ) {
          slotAvailability[index] = true;
        }
      });
    });
  }

  return slotAvailability.map((slot, index) => ({
    start_time: GRID_VIEW_TIME_SLOTS[index].start_time,
    end_time: GRID_VIEW_TIME_SLOTS[index].end_time,
    has_slot: slotStartingTimes?.length ? slot : false,
  }));
};

// function to extract partial time slots based on time window declared here
export const extractPartialTimeSlots = (
  appointmentSlots: Array<AppointmentSlot>,
  start_time: number,
  end_time: number
): Array<AppointmentSlot> => {
  let partialTimeSlots: Array<AppointmentSlot> = [];

  appointmentSlots.forEach((slot) => {
    if (
      +slot.start_time.split(':')[0] >= start_time &&
      +slot.start_time.split(':')[0] <= end_time
    ) {
      partialTimeSlots.push(slot);
    }
  });

  return partialTimeSlots;
};

// function to convert minutes to hours declared here
export const convertMinutesToHours = (mins: number): string => {
  let time: string = `${Math.trunc(mins / 60)} hr. ${mins % 60} min.`;

  return time;
};

// function to calculate leftover time declared here
export const calculateTimeLeft = (targetDate: string, targetTime: string) => {
  const difference = +new Date(`${targetDate} ${targetTime}`) - +new Date();

  let timeLeft: TimerData = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  if (difference > 0) {
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  return timeLeft;
};

// function to calculate whether the current time is upcoming or not declared here
export const isUpcomingTime = (date: string, time: string): boolean => {
  return +new Date(`${date} ${time}`) - +new Date() > 0;
};

// function to calculate whether the current time is running or not declared here
export const isRunningTime = (
  date: string,
  startingTime: string,
  endingTime: string
): boolean => {
  return (
    +new Date(`${date} ${startingTime}`) - +new Date() < 0 &&
    +new Date(`${date} ${endingTime}`) - +new Date() > 0
  );
};

// function to calculate whether the given time is past or not declared here
export const isPastTime = (date: string, time: string): boolean => {
  return +new Date(`${date} ${time}`) - +new Date() < 0;
};

// function to calculate duration in a selected time slot declared here
export const calculateTimeSlotDurationInMinutes = (
  start_time: string,
  end_time: string
): number => {
  const startTimeDateObj = new Date(`1970/01/01 ${start_time}`);
  const endTimeDateObj = new Date(`1970/01/01 ${end_time}`);

  return (+endTimeDateObj - +startTimeDateObj) / 1000 / 60;
};

// function to show duration in hours and minutes declared here
export const showDurationInHoursMinutes = (duration: number): string => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  return duration >= 60
    ? `${hours} ${hours > 1 ? 'Hours' : 'Hour'} ${
        minutes
          ? minutes > 1
            ? `${Math.floor(minutes % 60)} Minutes`
            : `${Math.floor(minutes % 60)} Minutes`
          : null
      } `
    : `${duration} Minutes`;
};

// function to calculate day progress in percentage declared here
export const calculateDayProgressPercentage = (
  startingDate: string,
  endingDate: string
) => {
  const startingDateMntObj = moment(startingDate, 'DD-MM-YYYY');
  const endingDateMntObj = moment(endingDate, 'DD-MM-YYYY');
  const currentDateMntObj = moment();

  const daysPast = currentDateMntObj.diff(startingDateMntObj, 'days');
  const totalDuration = endingDateMntObj.diff(startingDateMntObj, 'days');

  return (daysPast / totalDuration) * 100 > 100
    ? 100
    : (daysPast / totalDuration) * 100;
};

// function to calculate total duration in months declared here
export const calculateTotalDurationInMonths = (
  startingDate: string,
  endingDate: string
) => {
  const startingDateMntObj = moment(startingDate, 'DD-MM-YYYY');
  const endingDateMntObj = moment(endingDate, 'DD-MM-YYYY');

  const totalDays = endingDateMntObj.diff(startingDateMntObj, 'days');

  return Math.round(totalDays / 30);
};

// function to calculate how many days left from present date declared here
export const calculateDaysLeft = (endingDate: string) => {
  const endingDateMntObj = moment(endingDate, 'DD-MM-YYYY');
  const currentDateMntObj = moment();

  const daysLeft = endingDateMntObj.diff(currentDateMntObj, 'days');

  return daysLeft < 0 ? 0 : daysLeft;
};
